import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from '@/hooks/use-toast';
import { copyToClipboard } from '@/lib/clipboard';
import { useUserActivity } from '@/hooks/useUserActivity';
import type {
  WorkflowBlock,
  WorkflowState,
  MultiSheetWorkflowState,
  PortSide,
  BlockType,
} from '@/types/workflow';
import type { WorkflowKind } from '@/components/editor/dialogs/NewWorkflowDialog';
import type { useTranslation } from '@/i18n/LanguageContext';

type TFn = ReturnType<typeof useTranslation>['t'];
type TCountFn = ReturnType<typeof useTranslation>['tCount'];

type CreateConnectorResult = 'created' | 'removed' | 'noop' | 'rejected' | null | undefined | void;

interface UseInlineHandlersDeps {
  // Block ops
  blocks: WorkflowBlock[];
  createBlock: (type: BlockType, x: number, y: number) => WorkflowBlock;
  updateBlock: (id: string, patch: Partial<WorkflowBlock>) => void;
  deleteBlock: (id: string) => void;
  // Connector ops
  createConnector: (
    fromId: string,
    toId: string,
    fromPort?: PortSide,
    toPort?: PortSide,
  ) => CreateConnectorResult;
  // Selection
  setSelectedBlockId: (id: string | null) => void;
  setSelectedGroupId: (id: string | null) => void;
  // Workflow lifecycle
  workflowName: string;
  clearCanvas: () => void;
  setCurrentDocumentId: (id: string | null) => void;
  resetLayerPath: () => void;
  loadState: (state: WorkflowState | MultiSheetWorkflowState) => void;
  /** Append-läge för Import JSON — wrapar importen i en grupp och
   *  offset:ar ned-och-höger om befintlig bbox. */
  appendState: (
    state: WorkflowState | MultiSheetWorkflowState,
    opts?: { maxSheets?: number },
  ) => {
    idMap: Record<string, string>;
    wrapperGroupIds: string[];
    truncated: boolean;
    droppedCrossLinks: number;
  };
  /** Plan-limits så maxSheets-clamp:en kan följa user:s tier. */
  planLimits?: { maxSheets?: number };
  getState: () => WorkflowState | MultiSheetWorkflowState;
  // Dialogs
  openSaveDialogForUpdate: () => void;
  /** Direkt-uppdatera laddade cloud-kopian utan dialog. Returnerar
   *  true om en update gjordes; false om ingen doc är laddad. */
  quickUpdateToCloud: () => Promise<boolean>;
  // Logging / a11y
  logAction: (verb: string, details?: string) => void;
  announce: (msg: string) => void;
  // i18n + counters
  t: TFn;
  tCount: TCountFn;
  connectorsLength: number;
}

/**
 * Tio små glue-handlers som tidigare bodde inline i Index.tsx. De är för
 * små individuellt för att rättfärdiga egna filer, men tillsammans gör de
 * Index.tsx svår att läsa. Hooken är ren glue — ingen state utöver
 * `explainBlockId`-paret som hör till `handleExplainBlock`-flödet.
 *
 * Returnerar bag av callbacks + det enda explain-state-paret som JSX
 * behöver (för ExplainBlockPanel).
 */
export function useInlineHandlers(deps: UseInlineHandlersDeps) {
  const navigate = useNavigate();
  const {
    blocks, createBlock, updateBlock, deleteBlock,
    createConnector,
    setSelectedBlockId, setSelectedGroupId,
    workflowName, clearCanvas, setCurrentDocumentId, resetLayerPath,
    loadState, appendState, planLimits, getState,
    openSaveDialogForUpdate,
    quickUpdateToCloud,
    logAction, announce,
    t, tCount, connectorsLength,
  } = deps;

  // #96 Wave C.2 — opt-in event-tracking. Edge-fn:en no-op:ar
  // automatiskt om user inte är opted-in, så call-sites behöver inte
  // gate:a sina log()-anrop.
  const userActivity = useUserActivity();

  // ── Connector toggle ──────────────────────────────────────────
  const handleConnect = useCallback(
    (fromId: string, toId: string, fromPort?: PortSide, toPort?: PortSide) => {
      const result = createConnector(fromId, toId, fromPort, toPort);
      if (result === 'removed') {
        logAction('Connection removed');
        announce('Connection removed');
      } else if (result === 'created') {
        logAction('Connection created');
        announce('Connection created');
      } else if (result === 'rejected') {
        const msg = 'Porten används redan mellan dessa block — välj en annan port.';
        toast({ title: 'Anslutning skapades inte', description: msg, variant: 'destructive' });
        announce(msg);
      }
    },
    [createConnector, logAction, announce],
  );

  // ── Save-to-cloud ────────────────────────────────────────────
  // Om en doc är laddad → uppdatera direkt utan dialog. Annars öppna
  // dialogen så användaren kan namnge en ny rad.
  const handleSaveToCloud = useCallback(async () => {
    const didUpdate = await quickUpdateToCloud();
    if (!didUpdate) openSaveDialogForUpdate();
    // #96 Wave C.2: opt-in event-tracking. Edge-fn:en no-op:ar om
    // user inte är opted-in, så ingen FE-gate behövs. Fire-and-forget.
    if (didUpdate) {
      void userActivity.log('save', { blockCount: blocks.length, isCloud: true });
    }
  }, [quickUpdateToCloud, openSaveDialogForUpdate, userActivity, blocks.length]);

  // ── New workflow (type-chooser) ───────────────────────────────
  const handleNewWorkflowCreate = useCallback((kind: WorkflowKind) => {
    clearCanvas();
    setCurrentDocumentId(null);
    resetLayerPath();
    logAction('New workflow', `Type: ${kind}`);
    if (kind === 'coop') {
      toast({
        title: 'Nytt co-op-flöde',
        description: 'Spara först, klicka sedan "Dela arbetsyta" för att bjuda in editors.',
      });
    } else if (kind === 'process') {
      toast({
        title: 'Nytt process-flöde',
        description: 'Process-knappen aktiveras efter du sparat (kräver Enterprise).',
      });
    } else {
      toast({ title: 'Tom canvas', description: 'Bygg ditt flöde från scratch.' });
    }
  }, [clearCanvas, setCurrentDocumentId, resetLayerPath, logAction]);

  // ── Simulate ──────────────────────────────────────────────────
  const handleSimulate = useCallback(() => {
    toast({
      title: t('simulatingTitle'),
      description: `${t('simulatingDesc')} "${workflowName || 'Process'}" — ${tCount('blocksCount', blocks.length)}, ${tCount('connectorsCount', connectorsLength)}`,
    });
  }, [workflowName, blocks.length, connectorsLength, t, tCount]);

  // ── Import JSON ───────────────────────────────────────────────
  // Over-limit imports route to /preview (read-only sandbox) instead of
  // loading on the editing canvas. The preview page owns its own
  // workflow-state instance — when the user navigates back, the import
  // disappears entirely. No editing-jailbreak vector.
  const handleImportJson = useCallback(
    (
      state: WorkflowState | MultiSheetWorkflowState,
      mode: 'append' | 'replace',
      overLimit?: boolean,
    ) => {
      const isMulti = 'sheets' in state && Array.isArray((state as { sheets?: unknown }).sheets);

      if (mode === 'replace') {
        // Legacy-läge: skriver över hela canvas. overLimit → /preview.
        if (overLimit) {
          navigate('/flow-weaver/preview', { state: { workflow: state } });
          if (isMulti) {
            const multi = state as MultiSheetWorkflowState;
            logAction(
              'JSON import (preview-route)',
              `${multi.sheets.length} sheets, ${multi.crossSheetConnectors?.length ?? 0} cross-links`,
            );
          } else {
            const single = state as WorkflowState;
            logAction(
              'JSON import (preview-route)',
              `${tCount('blocksCount', single.blocks.length)}, ${tCount('connectorsCount', single.connectors.length)}, ${tCount('groupsCount', single.groups.length)}`,
            );
          }
          toast({
            title: 'Preview mode',
            description: 'Workflow:en överskrider plan-gränsen — visar i read-only preview.',
          });
          return;
        }

        loadState(state);
        setCurrentDocumentId(null);
        resetLayerPath();
        if (isMulti) {
          const multi = state as MultiSheetWorkflowState;
          logAction(
            'JSON import (replace, multi-sheet)',
            `${multi.sheets.length} sheets, ${multi.crossSheetConnectors?.length ?? 0} cross-links`,
          );
        } else {
          const single = state as WorkflowState;
          logAction(
            'JSON import (replace)',
            `${tCount('blocksCount', single.blocks.length)}, ${tCount('connectorsCount', single.connectors.length)}, ${tCount('groupsCount', single.groups.length)}`,
          );
        }
        toast({
          title: t('importedFromJson'),
          description: isMulti
            ? `${(state as MultiSheetWorkflowState).sheets.length} sheets`
            : `${tCount('blocksCount', (state as WorkflowState).blocks.length)}, ${tCount('connectorsCount', (state as WorkflowState).connectors.length)}, ${tCount('groupsCount', (state as WorkflowState).groups.length)}`,
        });
        return;
      }

      // Append-läge: lägger till content vid bottom-right av befintlig
      // canvas, wrapat i en "Imported"-grupp så user kan flytta hela
      // batchen som en enhet. overLimit fångas redan i dialogen så
      // append här är garanterat inom tier-bound.
      const result = appendState(state, { maxSheets: planLimits?.maxSheets });
      if (isMulti) {
        const multi = state as MultiSheetWorkflowState;
        logAction(
          'JSON import (append, multi-sheet)',
          `${multi.sheets.length} sheets added, ${result.wrapperGroupIds.length} wrappers`,
        );
      } else {
        const single = state as WorkflowState;
        logAction(
          'JSON import (append)',
          `${tCount('blocksCount', single.blocks.length)}, ${tCount('connectorsCount', single.connectors.length)}, ${tCount('groupsCount', single.groups.length)}`,
        );
      }
      if (result.truncated || result.droppedCrossLinks > 0) {
        toast({
          title: 'Importen klipptes',
          description: result.truncated
            ? `Tier-gränsen för max-sheets nåddes.${result.droppedCrossLinks > 0 ? ` ${result.droppedCrossLinks} cross-link(s) släpptes.` : ''}`
            : `${result.droppedCrossLinks} cross-link(s) dropped.`,
          variant: 'destructive',
        });
      } else {
        toast({
          title: 'Tillagt på canvas',
          description: isMulti
            ? `${(state as MultiSheetWorkflowState).sheets.length} sheets, ${result.wrapperGroupIds.length} grupp(er)`
            : `${tCount('blocksCount', (state as WorkflowState).blocks.length)}, ${tCount('connectorsCount', (state as WorkflowState).connectors.length)} — i en "Imported"-grupp`,
        });
      }
    },
    [navigate, loadState, appendState, planLimits, setCurrentDocumentId, resetLayerPath, logAction, t, tCount],
  );

  // ── Copy JSON (lean export) ───────────────────────────────────
  // Lager A (JSON-refactor): user-facing export är lean-format —
  // bara struktur (block, group, sheet, name, color, connection).
  // Descriptions / icons / metadata / attachments är medvetet
  // exkluderade så export blir kompakt och delbar utan att läcka
  // intern state. För AI-internal-pipeline används compact (se
  // aiAssistantDialog).
  const handleCopyJson = useCallback(async () => {
    const state = getState();
    try {
      const { toLeanExport } = await import('@/lib/leanExport');
      const lean = toLeanExport(state);
      await copyToClipboard(JSON.stringify(lean, null, 2));
      logAction('JSON copied', 'Workflow copied to clipboard (lean format)');
      toast({ title: t('copied'), description: t('jsonCopied') });
    } catch {
      toast({ title: t('error'), description: t('couldNotCopy'), variant: 'destructive' });
    }
  }, [getState, logAction, t]);

  // ── Focus a property field (right-click context menu) ─────────
  const handleFocusBlockProperty = useCallback(
    (blockId: string, field: 'name' | 'description' | 'color') => {
      setSelectedBlockId(blockId);
      setSelectedGroupId(null);
      requestAnimationFrame(() => requestAnimationFrame(() => {
        const target = field === 'color'
          ? document.querySelector<HTMLElement>('[data-guide="properties-color"] input[type="color"], [data-guide="properties-color"] input')
          : document.querySelector<HTMLElement>(`[data-guide="properties-${field}"] input, [data-guide="properties-${field}"] textarea`);
        target?.focus();
        if (target && 'select' in target && typeof (target as HTMLInputElement).select === 'function') {
          (target as HTMLInputElement).select();
        }
      }));
    },
    [setSelectedBlockId, setSelectedGroupId],
  );

  // ── Duplicate block (40px offset + (copy) suffix) ─────────────
  const handleDuplicateBlock = useCallback((blockId: string) => {
    const src = blocks.find(b => b.id === blockId);
    if (!src) return;
    const copy = createBlock(src.type, src.x + 40, src.y + 40);
    updateBlock(copy.id, {
      config: { ...src.config, name: `${src.config.name || 'Block'} (copy)` },
      color: src.color,
    });
    logAction('Block duplicated', src.config.name);
    announce(`Block duplicated: ${src.config.name}`);
  }, [blocks, createBlock, updateBlock, logAction, announce]);

  // ── Explain block (AI panel) ──────────────────────────────────
  const [explainBlockId, setExplainBlockId] = useState<string | null>(null);
  const handleExplainBlock = useCallback((blockId: string) => {
    setExplainBlockId(blockId);
    logAction('Block explain requested', blockId);
  }, [logAction]);
  const explainBlock = explainBlockId
    ? blocks.find((b) => b.id === explainBlockId) ?? null
    : null;

  // ── Delete block by id (no confirm) ───────────────────────────
  const handleDeleteBlockById = useCallback((blockId: string) => {
    deleteBlock(blockId);
    logAction('Block deleted', blockId);
  }, [deleteBlock, logAction]);

  return {
    handleConnect,
    handleSaveToCloud,
    handleNewWorkflowCreate,
    handleSimulate,
    handleImportJson,
    handleCopyJson,
    handleFocusBlockProperty,
    handleDuplicateBlock,
    handleExplainBlock,
    handleDeleteBlockById,
    explainBlock,
    explainBlockId,
    setExplainBlockId,
  };
}
